import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./RunnerGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { SettingsProvider } from "../../MathFactsGenerator/Settings/SettingsStore.tsx";
import { ValueRange } from "../../MathFactsGenerator/Settings/ValueRange.tsx";
import { canBuildDeck, flashCardSettings } from "../deck.ts";
import { createQuizQueue, QuizQueue } from "../Quiz/quizQueue.ts";
import { readBestScore, recordScore, runnerBestScoreKey } from "../Quiz/bestScore.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { RunnerGameRun, RunnerResult } from "./RunnerGameRun.tsx";
import { RunnerGameSummary } from "./RunnerGameSummary.tsx";
import { Pace, PACES, START_TRAVEL_MS } from "./runnerGame.ts";
import { runnerGameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface RunnerGamePageProps {
    slug: string;
    operation: Operations;
}

interface Summary {
    result: RunnerResult;
    best: number | null;
    isNewBest: boolean;
}

/** Each pace's name. `labelKey` so StringKeys.test.js sees the keys, as QuizPage's modes do. */
const PACE_LABELS: Record<Pace, { labelKey: StringKey }> = {
    jog: { labelKey: "flashCards.runnerPaceJog" },
    run: { labelKey: "flashCards.runnerPaceRun" },
    sprint: { labelKey: "flashCards.runnerPaceSprint" },
};

/**
 * Math Runner (ADR 0124): a keyword landing page around a three-lane runner on
 * the quiz's queue, marking and stores. A quiz route carrying `game: "runner"`;
 * its `quiz` is only the operation the picker opens on.
 */
export function RunnerGamePage({ slug, operation }: RunnerGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    const [pace, setPace] = useState<Pace | null>(null);
    const [queue, setQueue] = useState<QuizQueue | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [missesVersion, setMissesVersion] = useState(0);
    // Captured at the start, so a result is keyed by what was actually played.
    const [playedOperations, setPlayedOperations] = useState<string[]>([]);

    const ready = canBuildDeck(settings);
    const playing = pace !== null && queue !== null && summary === null;
    const plainQuiz = quizRoutes().find((route) => route.quiz === operation && !route.game);

    function start(chosen: Pace) {
        // Built here from settings as they stand, never during render: the
        // generator is non-deterministic (ADR 0001 §4).
        setQueue(createQuizQueue(settings));
        setPlayedOperations(operationsOf(settings));
        setPace(chosen);
        setSummary(null);
        setRunId((id) => id + 1);
    }

    function changePace() {
        setPace(null);
        setQueue(null);
        setSummary(null);
    }

    const handleFinish = useCallback(
        (result: RunnerResult) => {
            const key = runnerBestScoreKey(playedOperations, result.pace);
            const previous = readBestScore(key);
            const best = recordScore(key, result.score);
            // A wrong gate and a row with no pick are misses; a gate passed
            // clears like a right quiz answer (ADR 0124 §1, ADR 0012).
            result.misses.forEach((miss) => recordMiss(factKey(miss.front)));
            result.correctFronts.forEach((front) => recordCorrect(factKey(front)));
            setMissesVersion((v) => v + 1);
            // After the stores, as on the quiz: analytics never costs a best score.
            track("quiz_completed", runnerGameEventParams(playedOperations, result));
            setSummary({
                result,
                best: best > 0 ? best : null,
                isNewBest: previous !== null ? result.score > previous : result.score > 0,
            });
        },
        [playedOperations]
    );

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool">
                {summary ? (
                    <RunnerGameSummary
                        result={summary.result}
                        best={summary.best}
                        isNewBest={summary.isNewBest}
                        onPlayAgain={() => start(summary.result.pace)}
                        onChangePace={changePace}
                    />
                ) : playing ? (
                    <RunnerGameRun key={runId} queue={queue!} pace={pace!} onFinish={handleFinish} />
                ) : (
                    <div className="quizModePicker runnerIntro">
                        <p className="runnerIntroHow">{t("flashCards.runnerHowToPlay")}</p>
                        <p className="quizModePrompt">{t("flashCards.runnerWhichFacts")}</p>
                        <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.runnerWhichFacts")} />
                        <p className="quizModePrompt">{t("flashCards.runnerChoosePace")}</p>
                        {ready ? (
                            <div className="runnerPaces">
                                {PACES.map((option) => (
                                    <button key={option} type="button" onClick={() => start(option)}>
                                        <span className="runnerPaceName">{t(PACE_LABELS[option].labelKey)}</span>
                                        <span className="runnerPaceTime">
                                            {format(t("flashCards.runnerPaceSecondsTemplate"), {
                                                n: START_TRAVEL_MS[option] / 1000,
                                            })}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="quizEmpty">{t("flashCards.gameEmptyNote")}</p>
                        )}
                    </div>
                )}

                {!playing && plainQuiz && (
                    <p className="runnerCalmer">
                        {t("flashCards.gameCalmerPrefix")}
                        <a href={plainQuiz.slug}>{t("flashCards.gameCalmerLink")}</a>
                        {t("flashCards.gameCalmerSuffix")}
                    </p>
                )}

                {!playing && <MissedFactsPractice refreshKey={missesVersion} />}

                {!playing && (
                    <details className="quizSettings">
                        <summary>{t("flashCards.changeWhatToPractise")}</summary>
                        <div className="valueRangeContainer">
                            <ValueRange RangeSettings={0} />
                            <ValueRange RangeSettings={1} />
                        </div>
                    </details>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default RunnerGamePage;
